import { Injectable } from "@angular/core";
import { BackendService } from "../backend-service";
import { FilterSet } from "../../interfaces/filter-interface";
import { Pagination } from "../../interfaces/pagination";
import { HttpParams } from "@angular/common/http";
import {
  DevelopmentTile,
  MobilityTile,
  PersonalizedJourneyTile,
  TileInterface,
  TileStatusEnum,
  VideoResultInterface,
  VideoSearchInterface,
} from "../../interfaces/tile-interface";

@Injectable({ providedIn: "root" })
export class TilesApiService extends BackendService {
  constructor() {
    super();
  }

  async getTilesDevelopment(): Promise<DevelopmentTile[]> {
    return await this.get<DevelopmentTile[]>(`api/tiles/development`);
  }

  async getTilesMobility(): Promise<MobilityTile[]> {
    return await this.get<MobilityTile[]>(`api/tiles/mobility`);
  }

  async getVideoResult(
    selectedFilters: FilterSet[],
    currentPagination: Pagination
  ): Promise<VideoResultInterface> {
    const videoSearchData: VideoSearchInterface = {
      selectedFilters: selectedFilters,
      pagination: currentPagination,
    };
    return await this.post<VideoSearchInterface, VideoResultInterface>(
      `api/tiles/videos`,
      videoSearchData
    );
  }

  async getTilesJourney(immature: boolean): Promise<PersonalizedJourneyTile[]> {
    let params = new HttpParams();
    params = params.append("isImmature", immature);
    const response = await this.get<PersonalizedJourneyTile[]>(
      `api/tiles/journey`,
      params
    );
    return response;
  }

  pickVariant(index: number): TileInterface["variant"] {
    const variants: TileInterface["variant"][] = [
      "primary",
      "secondary",
      "tertiary",
      "quaternary",
      "quinary",
    ];
    return variants[index % variants.length];
  }

  updateTilesToShow(
    sourceTiles: TileInterface[],
    targetSignal: any,
    modeDraft: boolean
  ): void {
    const filteredTiles = modeDraft
      ? sourceTiles
      : sourceTiles.filter((tile) => tile.status === TileStatusEnum.Approved);

    const tilesWithSizes = filteredTiles.map((tile, index) => ({
      ...tile,
      size: this.giveSizeToTiles(index),
    }));
    targetSignal.set(tilesWithSizes);
  }

  giveSizeToTiles(index: number): "small" | "medium" {
    const tilePosition = (index + 1) % 4;
    if (tilePosition === 1 || tilePosition === 0) {
      return "medium";
    } else {
      return "small";
    }
  }

  mapCtaTextToUseBasedOnTileType(
    tile: DevelopmentTile | MobilityTile | PersonalizedJourneyTile
  ): string {
    if ("developmentTheme" in tile) {
      return "development.discoverCtaText";
    } else if ("mobilityTheme" in tile) {
      return "mobility.discoverCtaText";
    } else if ("personalizedJourneyTheme" in tile) {
      return "personalizedJourney.discoverCtaText";
    }

    return "";
  }

  mapTileToTileInterface(
    tile: DevelopmentTile | MobilityTile | PersonalizedJourneyTile,
    index: number = 0
  ): TileInterface {
    const ctaText = this.mapCtaTextToUseBasedOnTileType(tile);

    return {
      id: tile.id,
      title: tile.title,
      text: tile.text ?? undefined,
      ctaText: ctaText,
      ctaLink: tile.link,
      isExternalLink: tile.isExternalLink,
      image: tile.base64Image,
      variant: this.pickVariant(index + 1),
      size: this.giveSizeToTiles(index),
      textDisclaimer: tile.textDisclaimer ?? undefined,
      status: tile.tileStatus as TileStatusEnum,
    };
  }

  mapSearchTilesToTileInterface(
    tiles: DevelopmentTile[] | MobilityTile[] | PersonalizedJourneyTile[]
  ): TileInterface[] {
    if (!Array.isArray(tiles)) {
      return [];
    }

    return tiles.map((tile, index) => ({
      id: tile.id,
      title: tile.title,
      text: tile.description ?? tile.text ?? undefined,
      ctaText: "development.discoverCtaText",
      ctaLink: tile.url ?? tile.link ?? tile.videoUrl,
      isExternalLink: tile.isExternalLink ?? false,
      image: tile.base64Image ?? tile.thumbnailUrl ?? undefined,
      variant: this.pickVariant(index + 1),
      size: this.giveSizeToTiles(index),
      textDisclaimer: tile.textDisclaimer ?? undefined,
      status: (tile.tileStatus ??
        tile.status ??
        TileStatusEnum.Approved) as TileStatusEnum,
    }));
  }

  hasMeaningfulTextContent(content?: string): boolean {
    if (!content) {
      return false;
    }

    const normalizedContent = content.replace(/&nbsp;/gi, " ").trim();
    if (!normalizedContent) {
      return false;
    }

    const tempElement = document.createElement("div");
    tempElement.innerHTML = normalizedContent;

    const extractedText = (tempElement.textContent || "")
      .replace(/\u00A0/g, " ")
      .trim();

    return extractedText.length > 0;
  }
}
